import { StorageManager, StorageKey } from '@/@vxapp/@core/AppStorage/StorageManager'
import { createAuthChannel, sendAuthMessage } from '@/@vxapp/Auth/tabChannel'

const AUTH_REFRESH_BROWSER_LOCK = 'vxapp-auth-token-refresh'
const AUTH_REFRESH_LEASE_SETTLE_MS = 25

export const AuthChannelMessageType = Object.freeze({
  TOKEN_UPDATED: 'token-updated',
  LOGOUT_INTENT: 'logout-intent',
})

interface AuthRefreshLease {
  ownerId: string
}

interface AuthRefreshLockResult<T> {
  acquired: boolean
  result?: T
}

interface PeerTokenWaitOptions {
  pollIntervalMs?: number
  timeoutMs?: number
}

const createLeaseOwnerId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `auth-refresh-${Date.now()}-${Math.random().toString(36).slice(2)}`

const isOwnedLease = (value: unknown, ownerId: string): boolean =>
  typeof value === 'object' && value !== null && 'ownerId' in value && value.ownerId === ownerId

const releaseVisibleLease = (ownerId: string): void => {
  const storage = StorageManager()

  if (isOwnedLease(storage.get(StorageKey.RefreshInProg), ownerId)) {
    storage.remove(StorageKey.RefreshInProg)
  }
}

const runWithVisibleLease = async <T>(
  refresh: () => Promise<T>,
  forceLease: boolean
): Promise<AuthRefreshLockResult<T>> => {
  const storage = StorageManager()
  const lease: AuthRefreshLease = { ownerId: createLeaseOwnerId() }

  if (!forceLease && storage.isLockActive(StorageKey.RefreshInProg)) {
    return { acquired: false }
  }

  const leaseWritten = storage.set(StorageKey.RefreshInProg, lease, undefined, forceLease)

  if (!leaseWritten) {
    return { acquired: false }
  }

  if (!forceLease) {
    await new Promise(resolve => setTimeout(resolve, AUTH_REFRESH_LEASE_SETTLE_MS))

    if (!isOwnedLease(storage.get(StorageKey.RefreshInProg), lease.ownerId)) {
      return { acquired: false }
    }
  }

  try {
    return { acquired: true, result: await refresh() }
  } finally {
    releaseVisibleLease(lease.ownerId)
  }
}

export const withAuthRefreshLock = async <T>(
  refresh: () => Promise<T>
): Promise<AuthRefreshLockResult<T>> => {
  if (StorageManager().isLockActive(StorageKey.RefreshInProg)) {
    return { acquired: false }
  }

  const browserLocks =
    typeof navigator === 'undefined' || !('locks' in navigator) ? undefined : navigator.locks

  if (!browserLocks) {
    return runWithVisibleLease(refresh, false)
  }

  return browserLocks.request(AUTH_REFRESH_BROWSER_LOCK, { ifAvailable: true }, async lock => {
    if (!lock) {
      return { acquired: false }
    }

    return runWithVisibleLease(refresh, false)
  })
}

export const notifyAuthTokenUpdated = (): void => {
  const channel = createAuthChannel(() => {})

  if (!channel) {
    return
  }

  try {
    sendAuthMessage(channel, AuthChannelMessageType.TOKEN_UPDATED)
  } finally {
    channel.close()
  }
}

export const waitForPeerAuthToken = async (
  staleToken: string | null,
  readPersistedToken: () => string | null,
  options: PeerTokenWaitOptions = {}
): Promise<string | null> => {
  const { pollIntervalMs = 100, timeoutMs = 7000 } = options
  const initialToken = readPersistedToken()

  if (initialToken && initialToken !== staleToken) {
    return initialToken
  }

  return new Promise(resolve => {
    let isSettled = false
    let pollId: ReturnType<typeof setInterval> | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let channel: ReturnType<typeof createAuthChannel> = null

    const finish = (token: string | null) => {
      if (isSettled) {
        return
      }

      isSettled = true
      if (pollId) clearInterval(pollId)
      if (timeoutId) clearTimeout(timeoutId)
      channel?.close?.()
      resolve(token)
    }

    const resolvePersistedPeerToken = () => {
      const persistedToken = readPersistedToken()

      if (persistedToken && persistedToken !== staleToken) {
        finish(persistedToken)
      }
    }

    channel = createAuthChannel(message => {
      if (message?.type === AuthChannelMessageType.TOKEN_UPDATED) {
        resolvePersistedPeerToken()
      }
    })
    pollId = setInterval(resolvePersistedPeerToken, pollIntervalMs)
    timeoutId = setTimeout(() => finish(null), timeoutMs)
  })
}

interface AuthLogoutIntent {
  generation: string
  loginGeneration: string
  deadline: number
  loggedOut: boolean
  finished: boolean
  serverConfirmed?: boolean
}

export function readAuthLogoutIntent(): AuthLogoutIntent | null {
  return StorageManager().get<AuthLogoutIntent>(StorageKey.LogoutIntent)
}

export function authLoginGeneration(): string {
  return readAuthLogoutIntent()?.loginGeneration ?? 'initial'
}

export function isAuthLogoutPending(): boolean {
  return readAuthLogoutIntent()?.loggedOut === true
}

function notifyLogoutIntent(): void {
  const channel = createAuthChannel(() => {})
  if (!channel) return
  try {
    sendAuthMessage(channel, AuthChannelMessageType.LOGOUT_INTENT)
  } finally {
    channel.close()
  }
}

export function beginAuthLogout(): AuthLogoutIntent | null {
  const existing = readAuthLogoutIntent()
  if (existing?.loggedOut) return existing

  const intent: AuthLogoutIntent = {
    generation: createLeaseOwnerId(),
    loginGeneration: authLoginGeneration(),
    deadline: Date.now() + 10000,
    loggedOut: true,
    finished: false,
  }

  const storage = StorageManager()
  if (
    !storage.set(StorageKey.LogoutIntent, intent, 0, true, false) ||
    readAuthLogoutIntent()?.generation !== intent.generation
  )
    return null
  notifyLogoutIntent()

  return intent
}

export function finishAuthLogout(generation: string, serverConfirmed = false): void {
  const intent = readAuthLogoutIntent()
  if (intent?.generation !== generation || intent.finished) return
  StorageManager().set(
    StorageKey.LogoutIntent,
    { ...intent, finished: true, serverConfirmed },
    0,
    true,
    false
  )
  notifyLogoutIntent()
}

export function establishAuthLogin(): void {
  const generation = createLeaseOwnerId()

  StorageManager().set(
    StorageKey.LogoutIntent,
    {
      generation,
      loginGeneration: generation,
      deadline: 0,
      loggedOut: false,
      finished: true,
    },
    0,
    true,
    false
  )
}

export function canSaveRevocationCredential(loginGeneration: string): boolean {
  const intent = readAuthLogoutIntent()

  return Boolean(
    intent?.loggedOut &&
    !intent.finished &&
    intent.deadline > Date.now() &&
    intent.loginGeneration === loginGeneration
  )
}

export function observeAuthLogout(onIntent: () => void): () => void {
  let deadlineTimer: ReturnType<typeof setTimeout> | null = null
  let observedGeneration: string | null = null
  let observedCompletion = false

  const check = () => {
    if (deadlineTimer) clearTimeout(deadlineTimer)
    const intent = readAuthLogoutIntent()
    if (!intent?.loggedOut) return
    const complete = intent.finished || intent.deadline <= Date.now()
    if (observedGeneration !== intent.generation || (complete && !observedCompletion)) {
      observedGeneration = intent.generation
      observedCompletion = complete
      onIntent()
    }
    if (!complete) deadlineTimer = setTimeout(check, intent.deadline - Date.now())
  }

  const channel = createAuthChannel(message => {
    if (message?.type === AuthChannelMessageType.LOGOUT_INTENT) check()
  })

  globalThis.addEventListener?.('storage', check)
  globalThis.addEventListener?.('pageshow', check)
  globalThis.document?.addEventListener('visibilitychange', check)
  check()

  return () => {
    if (deadlineTimer) clearTimeout(deadlineTimer)
    channel?.close()
    globalThis.removeEventListener?.('storage', check)
    globalThis.removeEventListener?.('pageshow', check)
    globalThis.document?.removeEventListener('visibilitychange', check)
  }
}

export async function withAuthLogoutLock<T>(
  revoke: () => Promise<T>,
  deadline: number
): Promise<AuthRefreshLockResult<T>> {
  // Leave time to dispatch revocation after a bounded renewal finishes.
  const coordinationDeadline = deadline - 1000
  while (Date.now() < coordinationDeadline) {
    const result = await withAuthRefreshLock(async () => {
      if (Date.now() >= coordinationDeadline) return undefined

      return revoke()
    })

    if (result.acquired) return { acquired: result.result !== undefined, result: result.result }
    await new Promise(resolve => setTimeout(resolve, 50))
  }

  return { acquired: false }
}
